/**
 * basketCompareService — SuperAdmin cross-account basket/strategy/leg compare.
 *
 * Backs the SuperAdmin "Compare Baskets" page:
 *   - listBasketsForUser: reads any user's baskets via GET /baskets?acting_as=<id>
 *     (backend enforces super_admin_only=True on the basket routes).
 *   - compare: POST /admin/super/baskets/compare — serializes both baskets to
 *     the canonical export bundle and structurally diffs them server-side.
 */
import apiClient from './apiClient';
import { ApiResponse, Basket } from '../types';

const API_TYPE = 'api' as const;

/** A single field whose value differs between account A and B. */
export interface FieldDiff {
  field: string;
  a: unknown;
  b: unknown;
}

export interface LegDiffs {
  only_in_a: Array<number | string>;
  only_in_b: Array<number | string>;
  matched: Array<{ leg_index: number | string; fields: FieldDiff[] }>;
}

export interface StrategyDiff {
  strategy_name: string;
  fields: FieldDiff[];
  legs: LegDiffs;
}

export interface BundleDiff {
  identical: boolean;
  basket: FieldDiff[];
  strategies: {
    only_in_a: string[];
    only_in_b: string[];
    matched: StrategyDiff[];
  };
  summary: {
    basket_field_diffs: number;
    strategies_only_in_a: number;
    strategies_only_in_b: number;
    strategies_matched: number;
    strategies_with_diffs: number;
  };
}

export interface CompareSide {
  user_id: string;
  basket_id: string;
  bundle: Record<string, unknown>;
}

export interface CompareResult {
  a: CompareSide;
  b: CompareSide;
  diff: BundleDiff;
}

export interface CompareRequest {
  user_id_a: string;
  basket_id_a: string;
  user_id_b: string;
  basket_id_b: string;
}

export interface UserSearchResult {
  user_id: string;
  email: string | null;
  full_name?: string | null;
  zebu_client_id?: string | null;
}

function unwrap<T>(response: ApiResponse<T>, what: string): T {
  if (!response.success || response.data === undefined) {
    throw new Error(response.message || `Failed to ${what}`);
  }
  return response.data;
}

class BasketCompareService {
  /** Resolve users by (partial) email → {user_id, email, full_name}. */
  async searchUsers(q: string): Promise<UserSearchResult[]> {
    const qs = new URLSearchParams({ q }).toString();
    const response = await apiClient.get<ApiResponse<UserSearchResult[]>>(
      `/admin/super/users/search?${qs}`,
      undefined,
      API_TYPE,
    );
    return unwrap(response, 'search users') || [];
  }

  /** List a specific user's baskets (SuperAdmin acting-as that user). */
  async listBasketsForUser(userId: string): Promise<Basket[]> {
    const qs = new URLSearchParams({ acting_as: userId }).toString();
    const response = await apiClient.get<ApiResponse<Basket[]>>(
      `/baskets?${qs}`,
      undefined,
      API_TYPE,
    );
    return unwrap(response, 'load baskets') || [];
  }

  /** Diff one basket from account A against one from account B. */
  async compare(req: CompareRequest): Promise<CompareResult> {
    const response = await apiClient.post<ApiResponse<CompareResult>>(
      '/admin/super/baskets/compare',
      req,
      undefined,
      API_TYPE,
    );
    return unwrap(response, 'compare baskets');
  }
}

const basketCompareService = new BasketCompareService();
export default basketCompareService;
